import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { friendlyError } from "@/lib/errors";
import { cancelVoiceTelemetry, track } from "@/lib/events";
import { getVoiceAudioIssue, TRACK_MUTE_GRACE_MS } from "@/lib/voiceHealth";
import { nextSessionActive } from "@/lib/voiceStage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BAR_COUNT = 64;
const HEALTH_INTERVAL_MS = 10000;
const CLOSE_INTENTS = new Set(["user_stop", "navigation", "unmount", "remote_close", "startup_error"]);
const stoppedStreams = new WeakSet();
const closedContexts = new WeakSet();

const TERMINAL_CLOSE = {
  4401: { title: "Sua sessão de voz expirou", message: "Retome a voz para gerar uma nova sessão ou continue por texto nesta conversa." },
  4404: { title: "Não foi possível abrir esta conversa por voz", message: "Continue por texto ou volte à conversa correta antes de tentar novamente." },
  4408: { title: "A voz foi assumida em outro dispositivo", message: "Encerre a outra sessão antes de retomar aqui, ou continue por texto." },
  4429: { title: "Já existe uma sessão de voz ativa", message: "Encerre a sessão no outro dispositivo antes de retomar aqui, ou continue por texto." },
};

function monotonicNow() {
  const value = globalThis.performance?.now?.();
  return Number.isFinite(value) ? value : Date.now();
}

function newAttemptId() {
  try { return globalThis.crypto?.randomUUID?.() || null; } catch { return null; }
}

function byteLength(value) {
  if (typeof value?.byteLength === "number") return value.byteLength;
  if (typeof value?.size === "number") return value.size;
  return 0;
}

function knownState(value, allowed) {
  return allowed.includes(value) ? value : null;
}

function stopStream(stream) {
  if (!stream || stoppedStreams.has(stream)) return;
  stoppedStreams.add(stream);
  try { stream.getTracks?.().forEach((track) => track.stop()); } catch {}
}

function closeContext(context) {
  if (!context || closedContexts.has(context)) return;
  closedContexts.add(context);
  try { Promise.resolve(context.close?.()).catch(() => {}); } catch {}
}

function readLevels(analyser, buf, target) {
  if (!analyser || !buf) return;
  analyser.getByteFrequencyData(buf);
  const step = Math.max(1, Math.floor(buf.length / target.length));
  for (let i = 0; i < target.length; i += 1) {
    let sum = 0;
    const start = i * step;
    for (let j = 0; j < step; j += 1) sum += buf[Math.min(start + j, buf.length - 1)];
    target[i] = sum / step / 255;
  }
}

function recoveryForClose(attempt, event) {
  if (TERMINAL_CLOSE[event?.code]) return { ...TERMINAL_CLOSE[event.code], code: event.code };
  if (!attempt.readySeen) {
    return {
      title: "Não foi possível iniciar a conversa por voz",
      message: "Você pode tentar novamente ou continuar por texto na mesma conversa.",
      code: Number.isInteger(event?.code) ? event.code : null,
    };
  }
  if (event?.code === 1006 || event?.code === 1011 || attempt.socketErrored || attempt.fatalMessage) {
    return {
      title: "A conversa por voz foi interrompida",
      message: attempt.fatalMessage || "O conteúdo já salvo continua aqui. Retome a voz ou continue por texto.",
      code: Number.isInteger(event?.code) ? event.code : null,
    };
  }
  return {
    title: "A conversa por voz foi encerrada",
    message: "O encerramento não foi iniciado por esta tela. Retome a voz ou continue por texto.",
    code: Number.isInteger(event?.code) ? event.code : null,
  };
}

export default function useVoiceSession({ agent, threadId, onEvent, onGateError }) {
  const [status, setStatus] = useState("idle");
  const [active, setActive] = useState(false);
  const [recovery, setRecovery] = useState(null);
  const attemptRef = useRef(null);
  const generationRef = useRef(0);
  const configRef = useRef({ agent, threadId });
  const previousConfigRef = useRef({ agent, threadId });
  configRef.current = { agent, threadId };

  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const onGateRef = useRef(onGateError);
  onGateRef.current = onGateError;
  const statusRef = useRef(status);
  statusRef.current = status;

  const inputLevelsRef = useRef(new Array(BAR_COUNT).fill(0));
  const outputLevelsRef = useRef(new Array(BAR_COUNT).fill(0));
  const levelsRef = useRef(new Array(BAR_COUNT).fill(0));

  const isCurrent = useCallback((attempt) => (
    Boolean(attempt)
    && !attempt.closed
    && attemptRef.current === attempt
    && generationRef.current === attempt.generation
    && configRef.current.agent === attempt.agent
    && configRef.current.threadId === attempt.threadId
  ), []);

  const zeroLevels = useCallback(() => {
    for (let i = 0; i < BAR_COUNT; i += 1) {
      inputLevelsRef.current[i] = 0;
      outputLevelsRef.current[i] = 0;
      levelsRef.current[i] = 0;
    }
  }, []);

  const browserHealth = useCallback((attempt) => {
    const resources = attempt?.resources || {};
    const stream = resources.stream;
    const trackState = stream?.getAudioTracks?.()?.[0] || stream?.getTracks?.()?.[0] || null;
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;
    return {
      attempt_id: attempt?.attemptId ?? null,
      session_id: attempt?.sessionId ?? null,
      segment_index: attempt?.segmentIndex ?? null,
      thread_id: attempt?.threadId ?? null,
      agent: attempt?.agent ?? null,
      observed_at: new Date().toISOString(),
      mic_context_state: knownState(resources.micCtx?.state, ["suspended", "running", "closed", "interrupted"]),
      play_context_state: knownState(resources.playCtx?.state, ["suspended", "running", "closed", "interrupted"]),
      track_ready_state: knownState(trackState?.readyState, ["live", "ended"]),
      track_muted: typeof trackState?.muted === "boolean" ? trackState.muted : null,
      visibility_state: knownState(document.visibilityState, ["visible", "hidden", "prerender", "unloaded"]),
      online: typeof navigator.onLine === "boolean" ? navigator.onLine : null,
      effective_type: knownState(connection?.effectiveType, ["slow-2g", "2g", "3g", "4g"]),
      downlink: typeof connection?.downlink === "number" ? connection.downlink : null,
      rtt: typeof connection?.rtt === "number" ? connection.rtt : null,
      buffered_amount: typeof resources.ws?.bufferedAmount === "number" ? resources.ws.bufferedAmount : null,
      ...attempt?.counters,
    };
  }, []);

  const emitHealth = useCallback((attempt, reason) => {
    if (!isCurrent(attempt)) return;
    const now = monotonicNow();
    if (reason !== "periodic" && attempt.lastHealthAt != null && now - attempt.lastHealthAt < 1000) return;
    attempt.lastHealthAt = now;
    track("voice_audio_health", { ...browserHealth(attempt), reason }, { scope: attempt.telemetryScope });
  }, [browserHealth, isCurrent]);

  const emitClose = useCallback((attempt, intent, closeEvent = null) => {
    if (!attempt || attempt.closeEmitted) return;
    attempt.closeEmitted = true;
    cancelVoiceTelemetry(attempt.telemetryScope);
    track("voice_closed", {
      ...browserHealth(attempt),
      source: "client",
      intent: CLOSE_INTENTS.has(intent) ? intent : "user_stop",
      code: Number.isInteger(closeEvent?.code) ? closeEvent.code : null,
      was_clean: typeof closeEvent?.wasClean === "boolean" ? closeEvent.wasClean : null,
      duration_ms: Math.max(0, Math.round(monotonicNow() - attempt.startedAt)),
      ready_seen: attempt.readySeen,
    }, { scope: attempt.telemetryScope });
  }, [browserHealth]);

  const cleanupAttempt = useCallback((attempt, { closeSocket = true } = {}) => {
    if (!attempt || attempt.cleaned) return;
    attempt.cleaned = true;
    attempt.tokenController?.abort();
    attempt.tokenController = null;
    if (attempt.resources.healthTimer != null) clearInterval(attempt.resources.healthTimer);
    if (attempt.resources.muteTimer != null) clearTimeout(attempt.resources.muteTimer);
    if (attempt.resources.rafId != null) cancelAnimationFrame(attempt.resources.rafId);
    for (const remove of attempt.cleanupListeners) remove();
    attempt.cleanupListeners = [];
    const ws = attempt.resources.ws;
    if (ws) {
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      if (closeSocket) { try { ws.close(); } catch {} }
    }
    if (attempt.resources.micNode) attempt.resources.micNode.port.onmessage = null;
    if (attempt.resources.playNode) attempt.resources.playNode.port.onmessage = null;
    stopStream(attempt.resources.stream);
    closeContext(attempt.resources.micCtx);
    closeContext(attempt.resources.playCtx);
    zeroLevels();
  }, [zeroLevels]);

  const stopAttempt = useCallback((attempt, intent = "user_stop", options = {}) => {
    if (!attempt) return;
    const wasCurrent = attemptRef.current === attempt;
    emitClose(attempt, intent);
    attempt.closed = true;
    cleanupAttempt(attempt);
    if (wasCurrent) {
      attemptRef.current = null;
      generationRef.current += 1;
      setActive((value) => nextSessionActive(value, "stop"));
      setStatus("idle");
      if (!options.keepRecovery) setRecovery(null);
    }
  }, [cleanupAttempt, emitClose]);

  const stop = useCallback((intent = "user_stop") => {
    stopAttempt(attemptRef.current, CLOSE_INTENTS.has(intent) ? intent : "user_stop");
  }, [stopAttempt]);

  const dismissRecovery = useCallback(() => setRecovery(null), []);
  const continueWithText = useCallback(() => {
    stopAttempt(attemptRef.current, "user_stop");
    setRecovery(null);
  }, [stopAttempt]);

  const scheduleMeter = useCallback((attempt) => {
    const tick = () => {
      if (!isCurrent(attempt)) return;
      readLevels(attempt.resources.micAnalyser, attempt.resources.micBuf, inputLevelsRef.current);
      readLevels(attempt.resources.outAnalyser, attempt.resources.outBuf, outputLevelsRef.current);
      const source = statusRef.current === "speaking" ? outputLevelsRef.current : inputLevelsRef.current;
      for (let i = 0; i < BAR_COUNT; i += 1) levelsRef.current[i] = source[i];
      attempt.resources.rafId = requestAnimationFrame(tick);
    };
    attempt.resources.rafId = requestAnimationFrame(tick);
  }, [isCurrent]);

  const failStart = useCallback((attempt, err) => {
    if (!isCurrent(attempt)) { cleanupAttempt(attempt); return; }
    const detail = err?.response?.data?.detail ?? err?.response?.data ?? {};
    const gate = { code: detail.code, missing: detail.missing, cta: detail.cta };
    if (gate.code && onGateRef.current) onGateRef.current(gate);
    onEventRef.current?.({ type: "gate_error", ...gate });
    const denied = err?.name === "NotAllowedError" || err?.name === "SecurityError";
    const noMic = err?.name === "NotFoundError" || err?.name === "NotReadableError";
    const message = denied
      ? "Libere o acesso ao microfone para retomar a voz, ou continue por texto."
      : noMic
        ? "Não encontrei um microfone disponível. Você pode continuar por texto."
        : gate.code
          ? friendlyError({ response: { data: { detail: gate } } })
          : "A voz não iniciou. Tente novamente ou continue por texto nesta conversa.";
    if (denied || noMic) toast.error(message, { duration: 6000 });
    emitClose(attempt, "startup_error");
    attempt.closed = true;
    cleanupAttempt(attempt);
    if (attemptRef.current === attempt) {
      attemptRef.current = null;
      generationRef.current += 1;
      setActive((value) => nextSessionActive(value, "fatal"));
      setStatus("error");
      setRecovery({ title: "Não foi possível iniciar a conversa por voz", message, code: gate.code || null });
    }
  }, [cleanupAttempt, emitClose, isCurrent]);

  const failLiveTransport = useCallback((attempt, nextRecovery) => {
    if (!isCurrent(attempt)) return false;
    emitClose(attempt, "remote_close");
    attempt.closed = true;
    cleanupAttempt(attempt);
    attemptRef.current = null;
    generationRef.current += 1;
    setActive((value) => nextSessionActive(value, "fatal"));
    setStatus("error");
    setRecovery(nextRecovery);
    return true;
  }, [cleanupAttempt, emitClose, isCurrent]);

  const audioSnapshot = useCallback((attempt) => {
    const resources = attempt?.resources || {};
    const trackState = resources.stream?.getAudioTracks?.()?.[0]
      || resources.stream?.getTracks?.()?.[0]
      || null;
    return {
      micContextState: resources.micCtx?.state,
      playContextState: resources.playCtx?.state,
      trackReadyState: trackState?.readyState,
      trackMuted: attempt?.trackMuted || trackState?.muted === true,
      muteConfirmed: attempt?.muteConfirmed === true,
      online: typeof navigator.onLine === "boolean" ? navigator.onLine : undefined,
      canResumeMic: typeof resources.micCtx?.resume === "function",
      canResumePlayback: typeof resources.playCtx?.resume === "function",
    };
  }, []);

  const inspectAudioHealth = useCallback((attempt, reason, { clearTransient = false } = {}) => {
    if (!isCurrent(attempt)) return null;
    const issue = getVoiceAudioIssue(audioSnapshot(attempt));
    if (issue) {
      attempt.manualRecovery = false;
      attempt.audioIssue = issue;
      setStatus("error");
      setRecovery(issue);
    } else {
      const hadAudioIssue = Boolean(attempt.audioIssue);
      attempt.audioIssue = null;
      if (hadAudioIssue || clearTransient) {
        setRecovery((current) => (
          current?.source === "audio_health"
          || (clearTransient && current?.source === "voice_not_ready") ? null : current
        ));
      }
      if (!attempt.manualRecovery) {
        setStatus(attempt.readySeen ? (attempt.playbackActive ? "speaking" : "listening") : "connecting");
      }
    }
    if (reason) emitHealth(attempt, reason);
    return issue;
  }, [audioSnapshot, emitHealth, isCurrent]);

  const requestAudioResume = useCallback((attempt) => {
    if (!isCurrent(attempt)) return false;
    const contexts = [attempt.resources.playCtx, attempt.resources.micCtx];
    let requested = false;
    const report = () => inspectAudioHealth(attempt, null);
    const reject = () => {
      if (!isCurrent(attempt)) return;
      const issue = inspectAudioHealth(attempt, null);
      if (issue) {
        const failedIssue = {
          ...issue,
          message: "O navegador não ativou o áudio. Toque novamente em Ativar áudio ou continue por texto.",
        };
        attempt.audioIssue = failedIssue;
        setRecovery(failedIssue);
      }
    };
    for (const context of contexts) {
      if (!["suspended", "interrupted"].includes(context?.state) || typeof context.resume !== "function") continue;
      requested = true;
      try {
        Promise.resolve(context.resume()).then(report, reject);
      } catch {
        reject();
      }
    }
    Promise.resolve().then(report);
    return requested;
  }, [inspectAudioHealth, isCurrent]);

  const runAttempt = useCallback(async (attempt) => {
    let stream;
    let playCtx;
    let micCtx;
    try {
      // Construct/resume both contexts during the synchronous user gesture. The
      // microphone permission may remain open for longer than the 60 s token
      // lifetime, so the token is intentionally requested only after setup.
      playCtx = new AudioContext({ sampleRate: 24000 });
      attempt.resources.playCtx = playCtx;
      micCtx = new AudioContext();
      attempt.resources.micCtx = micCtx;

      const listen = (target, type, handler) => {
        if (!target?.addEventListener) return;
        target.addEventListener(type, handler);
        attempt.cleanupListeners.push(() => target.removeEventListener(type, handler));
      };
      const contextChanged = (reason) => () => {
        const issue = inspectAudioHealth(attempt, reason);
        if (issue?.action === "restart_session") failLiveTransport(attempt, issue);
      };
      listen(micCtx, "statechange", contextChanged("mic_context_statechange"));
      listen(playCtx, "statechange", contextChanged("play_context_statechange"));
      requestAudioResume(attempt);

      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      if (!isCurrent(attempt)) { stopStream(stream); return false; }
      attempt.resources.stream = stream;

      const audioTrack = stream.getAudioTracks?.()?.[0] || stream.getTracks?.()?.[0] || null;
      attempt.trackMuted = audioTrack?.muted === true;
      const trackEnded = () => {
        emitHealth(attempt, "track_ended");
        const issue = getVoiceAudioIssue({ ...audioSnapshot(attempt), trackReadyState: "ended" });
        failLiveTransport(attempt, issue);
      };
      const trackMuted = () => {
        if (!isCurrent(attempt)) return;
        attempt.trackMuted = true;
        attempt.muteConfirmed = false;
        if (attempt.resources.muteTimer != null) clearTimeout(attempt.resources.muteTimer);
        emitHealth(attempt, "track_muted");
        attempt.resources.muteTimer = setTimeout(() => {
          attempt.resources.muteTimer = null;
          if (!isCurrent(attempt) || !attempt.trackMuted) return;
          attempt.muteConfirmed = true;
          inspectAudioHealth(attempt, "track_muted");
        }, TRACK_MUTE_GRACE_MS);
      };
      const trackUnmuted = () => {
        if (!isCurrent(attempt)) return;
        attempt.trackMuted = false;
        attempt.muteConfirmed = false;
        if (attempt.resources.muteTimer != null) clearTimeout(attempt.resources.muteTimer);
        attempt.resources.muteTimer = null;
        inspectAudioHealth(attempt, "track_unmuted");
      };
      const visibilityChanged = () => {
        emitHealth(attempt, "visibilitychange");
        if (document.visibilityState === "visible") inspectAudioHealth(attempt, null);
      };
      const onlineChanged = () => inspectAudioHealth(attempt, navigator.onLine === false ? "offline" : "online");
      listen(audioTrack, "ended", trackEnded);
      listen(audioTrack, "mute", trackMuted);
      listen(audioTrack, "unmute", trackUnmuted);
      listen(document, "visibilitychange", visibilityChanged);
      listen(window, "online", onlineChanged);
      listen(window, "offline", onlineChanged);
      const initialIssue = getVoiceAudioIssue(audioSnapshot(attempt));
      if (audioTrack?.readyState === "ended" || initialIssue?.action === "restart_session") {
        failLiveTransport(attempt, initialIssue || getVoiceAudioIssue({ trackReadyState: "ended" }));
        return false;
      }
      if (attempt.trackMuted) trackMuted();
      else inspectAudioHealth(attempt, null);

      await playCtx.audioWorklet.addModule("/playback-processor.js");
      if (!isCurrent(attempt)) { closeContext(playCtx); return false; }
      const playNode = new AudioWorkletNode(playCtx, "playback-processor");
      const outAnalyser = playCtx.createAnalyser();
      outAnalyser.fftSize = 256;
      outAnalyser.smoothingTimeConstant = 0.75;
      playNode.connect(outAnalyser);
      outAnalyser.connect(playCtx.destination);
      attempt.resources.playNode = playNode;
      attempt.resources.outAnalyser = outAnalyser;
      attempt.resources.outBuf = new Uint8Array(outAnalyser.frequencyBinCount);

      await micCtx.audioWorklet.addModule("/mic-processor.js");
      if (!isCurrent(attempt)) { closeContext(micCtx); return false; }
      const source = micCtx.createMediaStreamSource(stream);
      const micNode = new AudioWorkletNode(micCtx, "mic-processor");
      const micAnalyser = micCtx.createAnalyser();
      micAnalyser.fftSize = 256;
      micAnalyser.smoothingTimeConstant = 0.75;
      source.connect(micAnalyser);
      const mute = micCtx.createGain();
      mute.gain.value = 0;
      source.connect(micNode);
      micNode.connect(mute);
      mute.connect(micCtx.destination);
      attempt.resources.micNode = micNode;
      attempt.resources.micAnalyser = micAnalyser;
      attempt.resources.micBuf = new Uint8Array(micAnalyser.frequencyBinCount);

      attempt.tokenController = typeof AbortController === "function" ? new AbortController() : null;
      const { data } = await api.post("/voice/session", {
        agent: attempt.agent, thread_id: attempt.threadId, attempt_id: attempt.attemptId,
      }, attempt.tokenController ? { signal: attempt.tokenController.signal } : undefined);
      attempt.tokenController = null;
      if (!isCurrent(attempt)) return false;

      const params = new URLSearchParams({ token: data.voice_token, agent: attempt.agent });
      if (attempt.threadId) params.set("thread_id", attempt.threadId);
      const ws = new WebSocket(`${BACKEND_URL.replace(/^http/, "ws")}/api/voice/ws?${params}`);
      ws.binaryType = "arraybuffer";
      attempt.resources.ws = ws;

      micNode.port.onmessage = (event) => {
        if (!isCurrent(attempt)) return;
        const bytes = byteLength(event.data);
        attempt.counters.mic_frames += 1;
        attempt.counters.mic_bytes += bytes;
        if (ws.readyState === WebSocket.OPEN && !attempt.uplinkFailed) {
          try {
            ws.send(event.data);
            attempt.counters.socket_send_frames += 1;
            attempt.counters.socket_send_bytes += bytes;
          } catch {
            attempt.uplinkFailed = true;
            attempt.socketErrored = true;
            failLiveTransport(attempt, {
              title: "A conversa por voz foi interrompida",
              message: "O áudio não pôde ser enviado. Retome a voz ou continue por texto.",
              code: null,
            });
          }
        }
      };
      playNode.port.onmessage = (event) => {
        if (!isCurrent(attempt)) return;
        if (event.data === "active") {
          attempt.counters.playback_started_count += 1;
          attempt.playbackActive = true;
          if (!attempt.audioIssue && !attempt.manualRecovery) setStatus("speaking");
          emitHealth(attempt, "playback_active");
        } else if (event.data === "idle") {
          attempt.playbackActive = false;
          if (!attempt.audioIssue && !attempt.manualRecovery) {
            setStatus((value) => (value === "speaking" ? "listening" : value));
          }
          emitHealth(attempt, "playback_idle");
        }
      };

      ws.onmessage = (event) => {
        if (!isCurrent(attempt)) return;
        if (typeof event.data === "string") {
          let evt;
          try { evt = JSON.parse(event.data); } catch { return; }
          if (evt.type === "ready") {
            attempt.readySeen = true;
            attempt.sessionId = evt.session_id ?? attempt.sessionId;
            attempt.segmentIndex = evt.segment_index ?? attempt.segmentIndex;
            inspectAudioHealth(attempt, "ready", { clearTransient: true });
          } else if (evt.type === "reconnecting") {
            if (!attempt.audioIssue && !attempt.manualRecovery) setStatus("reconnecting");
            emitHealth(attempt, "reconnecting");
          } else if (evt.type === "interrupted") {
            playNode.port.postMessage("clear");
            attempt.playbackActive = false;
            if (!attempt.audioIssue && !attempt.manualRecovery) setStatus("listening");
          } else if (evt.type === "error") {
            attempt.fatalMessage = evt.message || "A conversa por voz foi interrompida.";
          }
          onEventRef.current?.(evt);
          return;
        }
        const bytes = byteLength(event.data);
        attempt.counters.browser_audio_frames_received += 1;
        attempt.counters.browser_audio_bytes_received += bytes;
        playNode.port.postMessage(event.data, [event.data]);
        attempt.counters.playback_enqueue_frames += 1;
        attempt.counters.playback_enqueue_bytes += bytes;
      };
      ws.onerror = () => { if (isCurrent(attempt)) attempt.socketErrored = true; };
      ws.onclose = (event) => {
        if (!isCurrent(attempt)) { emitClose(attempt, "remote_close", event); return; }
        const nextRecovery = recoveryForClose(attempt, event);
        emitClose(attempt, "remote_close", event);
        attempt.closed = true;
        cleanupAttempt(attempt, { closeSocket: false });
        attemptRef.current = null;
        generationRef.current += 1;
        setActive((value) => nextSessionActive(value, "stop"));
        setStatus(nextRecovery ? "error" : "idle");
        setRecovery(nextRecovery);
        onEventRef.current?.({
          type: "voice_closed", code: Number.isInteger(event?.code) ? event.code : null,
          terminal: Boolean(TERMINAL_CLOSE[event?.code]), recovery: nextRecovery,
        });
      };

      attempt.resources.healthTimer = setInterval(() => emitHealth(attempt, "periodic"), HEALTH_INTERVAL_MS);
      scheduleMeter(attempt);
      return true;
    } catch (err) {
      if (attempt.closed || !isCurrent(attempt)) {
        stopStream(stream);
        closeContext(micCtx);
        closeContext(playCtx);
        return false;
      }
      failStart(attempt, err);
      return false;
    }
  }, [audioSnapshot, cleanupAttempt, emitClose, emitHealth, failLiveTransport, failStart,
    inspectAudioHealth, isCurrent, requestAudioResume, scheduleMeter]);

  const start = useCallback(() => {
    const existing = attemptRef.current;
    if (existing && !existing.closed) {
      if (existing.agent === agent && existing.threadId === threadId) return existing.startPromise;
      stopAttempt(existing, "navigation");
    }
    const attempt = {
      generation: generationRef.current + 1,
      attemptId: newAttemptId(), telemetryScope: Symbol("voice-attempt"), agent, threadId,
      startedAt: monotonicNow(), lastHealthAt: null, readySeen: false, closed: false,
      cleaned: false, closeEmitted: false, socketErrored: false, uplinkFailed: false, fatalMessage: null,
      audioIssue: null, manualRecovery: false, playbackActive: false, trackMuted: false, muteConfirmed: false,
      sessionId: null, segmentIndex: null, tokenController: null, cleanupListeners: [],
      resources: {
        stream: null, playCtx: null, micCtx: null, playNode: null, micNode: null,
        outAnalyser: null, micAnalyser: null, outBuf: null, micBuf: null,
        ws: null, rafId: null, healthTimer: null, muteTimer: null,
      },
      counters: {
        mic_frames: 0, mic_bytes: 0, socket_send_frames: 0, socket_send_bytes: 0,
        browser_audio_frames_received: 0, browser_audio_bytes_received: 0,
        playback_enqueue_frames: 0, playback_enqueue_bytes: 0, playback_started_count: 0,
      },
    };
    generationRef.current = attempt.generation;
    attemptRef.current = attempt;
    setRecovery(null);
    setActive((value) => nextSessionActive(value, "start"));
    setStatus("connecting");
    attempt.startPromise = runAttempt(attempt);
    return attempt.startPromise;
  }, [agent, runAttempt, stopAttempt, threadId]);

  const sendText = useCallback((text) => {
    const attempt = attemptRef.current;
    const ws = attempt?.resources.ws;
    if (
      isCurrent(attempt) && attempt.readySeen
      && attempt.threadId === configRef.current.threadId
      && ws?.readyState === WebSocket.OPEN && text?.trim()
    ) {
      try {
        ws.send(JSON.stringify({ type: "text", text }));
        return true;
      } catch {
        attempt.socketErrored = true;
        failLiveTransport(attempt, {
          title: "A conversa por voz foi interrompida",
          message: "A mensagem foi preservada. Retome a voz ou continue por texto.",
          code: null,
        });
        return false;
      }
    }
    if (attempt && !attempt.closed) {
      setRecovery({
        source: "voice_not_ready",
        title: "A voz ainda não está pronta para receber texto",
        message: "A mensagem foi preservada. Aguarde, retome a voz ou continue por texto.",
        code: null,
      });
    }
    return false;
  }, [failLiveTransport, isCurrent]);

  const reportAudioProblem = useCallback(() => {
    const attempt = attemptRef.current;
    if (!isCurrent(attempt)) return false;
    attempt.manualRecovery = true;
    setStatus("error");
    setRecovery({
      source: "manual_audio_help",
      kind: "unknown_audio",
      title: "A voz não respondeu",
      message: "Não há sinal suficiente para identificar a causa. Retome a voz ou continue por texto sem perder o que digitou.",
      action: "restart_session",
      actionLabel: "Retomar voz",
      code: null,
    });
    return true;
  }, [isCurrent]);

  const resume = useCallback(() => {
    const attempt = attemptRef.current;
    if (attempt?.audioIssue?.action === "resume_contexts") return requestAudioResume(attempt);
    if (attempt) stopAttempt(attempt, "user_stop");
    setRecovery(null);
    return start();
  }, [requestAudioResume, start, stopAttempt]);

  const stopRef = useRef(stop);
  stopRef.current = stop;
  useEffect(() => () => stopRef.current("unmount"), []);
  useEffect(() => {
    const previous = previousConfigRef.current;
    const changed = previous.agent !== agent || previous.threadId !== threadId;
    previousConfigRef.current = { agent, threadId };
    if (!changed) return;
    const attempt = attemptRef.current;
    if (attempt && (attempt.agent !== agent || attempt.threadId !== threadId)) stopAttempt(attempt, "navigation");
    else {
      setRecovery(null);
      setStatus("idle");
      setActive(false);
    }
  }, [agent, stopAttempt, threadId]);
  useEffect(() => {
    const navigate = () => stopRef.current("navigation");
    window.addEventListener("pagehide", navigate);
    return () => window.removeEventListener("pagehide", navigate);
  }, []);

  return {
    status, start, resume, stop, sendText, active, recovery, dismissRecovery, continueWithText,
    reportAudioProblem,
    levels: levelsRef.current,
    inputLevels: inputLevelsRef.current,
    outputLevels: outputLevelsRef.current,
  };
}
